<template>
  <b-modal
    id="news-modal"
    :visible="visible"
    :title="$t('newsModal.title')"
    scrollable
    centered
    size="lg"
    @hidden="$emit('hidden')"
  >
    <div v-if="localizedEntries.length === 0" class="text-center py-4">
      {{ $t('newsModal.empty') }}
      <a :href="changelogUrl" target="_blank" rel="noopener">help.webmag.io</a>
    </div>
    <div
      v-for="(entry, index) in localizedEntries"
      :key="entry.slug"
      class="news-entry pb-3 mb-3"
      :class="{ 'border-bottom': index !== localizedEntries.length - 1 }"
    >
      <a
        v-if="entry.imageUrl"
        :href="entry.helpUrl"
        target="_blank"
        rel="noopener"
      >
        <img
          :src="entry.imageUrl"
          :alt="entry.title"
          class="news-image mb-3"
          loading="lazy"
        />
      </a>
      <h5 class="mb-1">
        {{ entry.title }}
        <span v-if="entry.isNew" class="news-new">{{ $t('newsModal.new') }}</span>
      </h5>
      <p class="news-meta small mb-2">
        {{ formatDate(entry.publishedAt) }}
        <span v-if="entry.version"> &ndash; {{ $t('newsModal.versionPrefix') }} {{ entry.version }}</span>
        <b-badge v-if="entry.category" variant="light" class="ml-2">{{ entry.category }}</b-badge>
      </p>
      <p class="mb-1">{{ entry.teaser }}</p>
      <a :href="entry.helpUrl" target="_blank" rel="noopener">{{ $t('newsModal.readMore') }}</a>
    </div>
    <div v-if="localizedEntries.length" class="news-all-updates text-center mt-3">
      <a :href="changelogUrl" target="_blank" rel="noopener">{{ $t('newsModal.allUpdates') }}</a>
    </div>
    <template #modal-footer>
      <div class="news-feedback-bar d-flex align-items-center justify-content-between w-100">
        <span class="news-feedback-text">{{ $t('newsModal.feedbackPrompt') }}</span>
        <b-button
          size="sm"
          variant="success"
          class="news-feedback-button"
          @click="$emit('feedback')"
        >
          {{ $t('newsModal.feedbackButton') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { isEntryUnseen, localizeEntry } from '@/utils/changelogNews';

export default {
  name: 'ModalNews',
  props: {
    visible: { type: Boolean, default: false },
    entries: { type: Array, default: () => [] },
    language: { type: String, default: 'en' },
    seenBefore: { type: String, default: null },
    userCreatedAt: { type: String, default: null },
  },
  data() {
    return {
      // changelog overview page ("Alle Updates ansehen" + empty state);
      // individual entries link to /docs/changelog/<slug>/ via help_url
      changelogUrl: 'https://help.webmag.io/changelog/',
    };
  },
  computed: {
    localizedEntries() {
      return this.entries.map((entry) => ({
        ...localizeEntry(entry, this.language),
        isNew: isEntryUnseen(entry, this.seenBefore, this.userCreatedAt),
      }));
    },
  },
  methods: {
    formatDate(dateString) {
      const locale = (this.language === 'de') ? 'de-DE' : 'en-US';
      // published_at is a plain date — render it in UTC, otherwise users
      // west of UTC would see the previous day
      return new Date(dateString).toLocaleDateString(locale, {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
      });
    },
  },
};
</script>

<style scoped lang="scss">
// the global _modal.scss zeroes .modal-body padding for all dialogs —
// restore comfortable spacing for the news list
::v-deep .modal-body {
  padding: 24px 32px;
}

.news-entry {
  .news-image {
    display: block;
    width: auto;
    max-width: 100%;
    max-height: 260px;
    margin: 0 auto;
    border-radius: 6px;
    border: 1px solid #e0e2e4;
  }

  h5 {
    font-size: 22px;
    font-weight: 600;
    color: #35393e;
  }

  ::v-deep p {
    color: #5a5e62;
  }

  .news-new {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 999px;
    background-color: #4adc8e;
    color: #0f3b26; // dark green: white on this green would be too faint
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    vertical-align: middle;
  }

  .news-meta {
    color: #9a9da0;
  }

  a {
    font-weight: 600;
    color: $webmag-green-light;

    &:hover {
      color: $webmag-green-light;
      text-decoration: underline;
    }
  }
}

.news-all-updates a {
  color: #9a9da0;

  &:hover {
    color: #35393e;
  }
}

// the global _modal.scss styles .modal-footer for the old two-button
// dialogs (padding 0, 61px buttons) — restore a normal bar here
::v-deep .modal-footer {
  padding: 12px 32px;
  height: auto;
  background-color: #faf9fa;

  .news-feedback-text {
    color: #5a5e62;
  }

  // the global sheet paints .modal-footer buttons light-gray with
  // !important hover colors — force the green primary look back
  button.news-feedback-button {
    height: auto;
    margin: 0;
    padding: 6px 16px;
    background-color: $webmag-green-light !important;
    border-color: $webmag-green-light !important;
    color: #fff !important;

    &:hover, &:focus, &:active {
      background-color: darken($webmag-green-light, 8%) !important;
      border-color: darken($webmag-green-light, 8%) !important;
      color: #fff !important;
    }
  }
}
</style>
